import React, { useState, useEffect } from "react";
import { Grid, TextField, Box, Button, Typography } from "@mui/material";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import OnDutyActionMenu from "./OnDutyActionMenu";
import { API_ENDPOINTS } from "../../../config/api/apiCall";

/* ---------- STYLES ---------- */
const LABEL_STYLE = { fontSize: "0.85rem", color: "#ffffff" };

const INPUT_PROPS = {
  size: "small",
  fullWidth: true,
  sx: {
    "& .MuiInputBase-input": { color: "#ffffff", fontSize: "0.85rem" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#777" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#aaa" }
  }
};

const BUTTON_STYLE = {
  fontSize: "0.8rem",
  color: "#ffffff",
  textTransform: "none",
  borderColor: "#aaa"
};

//const EMPTY_EXPENSE = { expence_type: "", amount: "", bill_attachment: null };
const EMPTY_EXPENSE = {
  expense_id: 0,
  expence_type: "",
  amount: "",
  bill_attachment: null,
  existing_attachment: null
};
/* ---------- COMPONENT ---------- */
const AddUpdateOnDuty = ({ formData, setIsFormOpen, reloadList }) => {
  const [data, setData] = useState({});
  const [expenses, setExpenses] = useState([EMPTY_EXPENSE]);

  /* ---------- LOAD ADD / EDIT DATA ---------- */
  useEffect(() => {
    if (!formData) {
      // ADD MODE
      setData({});
      setExpenses([EMPTY_EXPENSE]);
      return;
    }

    const record = Array.isArray(formData) ? formData[0] : formData;

    setData(record);

    if (record.expense_list?.length) {
      setExpenses(
        record.expense_list.map(e => ({
          expense_id:e.expense_id|| "",
          expence_type: e.expence_type || "",
          amount: e.amount || "",
          bill_attachment: null,                // new upload only
          existing_attachment: e.bill_attachement // base64 from DB
        }))
      );
    } else {
      setExpenses([EMPTY_EXPENSE]);
    }
  }, [formData]);

  const isEdit = Boolean(data?.id_on_duty);

  /* ---------- EXPENSE HANDLERS ---------- */
  const handleExpenseChange = (idx, field, value) => {
    const updated = [...expenses];
    updated[idx][field] = value;
    setExpenses(updated);
  };

  const handleFileChange = (idx, file) => {
    const updated = [...expenses];
    updated[idx].bill_attachment = file;
    setExpenses(updated);
  };

  const addExpenseRow = () =>
    setExpenses([...expenses, EMPTY_EXPENSE]);

  /* ---------- VIEW ATTACHMENT (ON CLICK ONLY) ---------- */
  const openAttachment = (base64, expenseType = "expense", amount = "0") => {
    if (!base64) return;

    try {
      const cleanBase64 = base64.replace(/\s/g, "");
      const byteCharacters = atob(cleanBase64);
      const byteNumbers = Array.from(byteCharacters, (c) => c.charCodeAt(0));
      let mimeType = "text/plain"; // default

      // Simple detection: PDF files start with '%PDF'
      if (byteCharacters.startsWith("%PDF")) mimeType = "application/pdf";

      const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
      const url = URL.createObjectURL(blob);

      // Open in new tab
      const newWindow = window.open(url, "_blank");
      if (!newWindow) {
        alert("Please allow popups to view the attachment.");
        return;
      }

      // Optionally, set filename for download
      const filename = `${expenseType}_${amount}${mimeType === "application/pdf" ? ".pdf" : ".txt"}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      // Add a small delay to allow tab to open before triggering download if needed
      setTimeout(() => a.click(), 500);
    } catch (err) {
      console.error("Failed to open attachment:", err);
    }
  };

  /* ---------- FILE → BASE64 (SAVE ONLY) ---------- */
  const fileToBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
    });

  /* ---------- SAVE ---------- */
 const handleSave = async () => {
  // ----- BASIC VALIDATIONS -----
  if (!data.travel_purpose?.trim()) {
    alert("Travel Purpose is required");
    return;
  }

  if (!data.location?.trim()) {
    alert("Location is required");
    return;
  }

  if (!data.on_duty_from_date || !data.on_duty_to_date) {
    alert("Both From Date and To Date are required");
    return;
  }

  const fromDate = dayjs(data.on_duty_from_date);
  const toDate = dayjs(data.on_duty_to_date);

  if (fromDate.isAfter(toDate)) {
    alert("From Date cannot be later than To Date");
    return;
  }

  for (let i = 0; i < expenses.length; i++) {
    const exp = expenses[i];
    if (!exp.expence_type?.trim()) {
      alert(`Expense Type is required for row ${i + 1}`);
      return;
    }

    if (!exp.amount || isNaN(exp.amount) || Number(exp.amount) <= 0) {
      alert(`Amount must be a number > 0 for row ${i + 1}`);
      return;
    }
  }

  // ----- PROCESS ATTACHMENTS -----
  const expensePayload = await Promise.all(
    expenses.map(async e => {
      const isExisting = Number(e.expense_id) > 0;
      let attachmentBase64 = null;

      if (e.bill_attachment) {
        attachmentBase64 = await fileToBase64(e.bill_attachment);
      } else if (isExisting && e.existing_attachment) {
        attachmentBase64 = e.existing_attachment;
      }

      return {
        expense_id: isExisting ? Number(e.expense_id) : 0,
        expence_type: e.expence_type,
        amount: Number(e.amount),
        bill_attachement: attachmentBase64
      };
    })
  );

  // ----- SAVE PAYLOAD -----
  const payload = {
    p_company_id: Number(localStorage.getItem("companyid")),
    p_plant_id: 1,
    p_id_employee: 3,
    p_id_on_duty: data.id_on_duty || 0,
    p_travel_purpose: data.travel_purpose,
    p_location: data.location,
    p_from_date: dayjs(data.on_duty_from_date).format("YYYY-MM-DD"),
    p_to_date: dayjs(data.on_duty_to_date).format("YYYY-MM-DD"),
    p_created_by: 1,
    p_expenses: expensePayload
  };

  await fetch(API_ENDPOINTS.onduty.save, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });

  reloadList();
  setIsFormOpen(false);
};

// const handleSave = async () => {
//   const expensePayload = await Promise.all(
//     expenses.map(async e => {
//       const isExisting = Number(e.expense_id) > 0;

//       let attachmentBase64 = null;

//       if (e.bill_attachment) {
//         // user uploaded NEW file
//         attachmentBase64 = await fileToBase64(e.bill_attachment);
//       } else if (isExisting && e.existing_attachment) {
//         // existing expense, no new upload → resend existing file
//         attachmentBase64 = e.existing_attachment;
//       }

//       return {
//         expense_id: isExisting ? Number(e.expense_id) : 0,
//         expence_type: e.expence_type,
//         amount: Number(e.amount),
//         bill_attachement: attachmentBase64
//       };
//     })
//   );

//   const payload = {
//     p_company_id: Number(localStorage.getItem("companyid")),
//     p_plant_id: 1,
//     p_id_employee: 3,
//     p_id_on_duty: data.id_on_duty || 0,
//     p_travel_purpose: data.travel_purpose || "",
//     p_location: data.location || "",
//     p_from_date: data.on_duty_from_date
//       ? dayjs(data.on_duty_from_date).format("YYYY-MM-DD")
//       : null,
//     p_to_date: data.on_duty_to_date
//       ? dayjs(data.on_duty_to_date).format("YYYY-MM-DD")
//       : null,
//     p_created_by: 1,
//     p_expenses: expensePayload
//   };

//   await fetch(API_ENDPOINTS.onduty.save, {
//     method: "POST",
//     headers: { "Content-Type": "application/json" },
//     body: JSON.stringify(payload)
//   });

//   reloadList();
//   setIsFormOpen(false);
// };

  /* ---------- UI ---------- */
  return (
    <Box>
      <OnDutyActionMenu
        isEdit={isEdit}
        onSave={handleSave}
        onDelete={() => {}}
        onClear={() => {
          setData({});
          setExpenses([EMPTY_EXPENSE]);
        }}
        onExit={() => setIsFormOpen(false)}
      />

      <Box p={2}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Travel Purpose"
              {...INPUT_PROPS}
              InputLabelProps={{ sx: LABEL_STYLE }}
              value={data.travel_purpose || ""}
              onChange={e =>
                setData({ ...data, travel_purpose: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Location"
              {...INPUT_PROPS}
              InputLabelProps={{ sx: LABEL_STYLE }}
              value={data.location || ""}
              onChange={e =>
                setData({ ...data, location: e.target.value })
              }
            />
          </Grid>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid item xs={6}>
              <DatePicker
                label="From Date"
                value={data.on_duty_from_date ? dayjs(data.on_duty_from_date) : null}
                onChange={val =>
                  setData({
                    ...data,
                    on_duty_from_date: val ? val.format("YYYY-MM-DD") : null
                  })
                }
                slotProps={{
                  textField: { ...INPUT_PROPS, InputLabelProps: { sx: LABEL_STYLE } }
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <DatePicker
                label="To Date"
                value={data.on_duty_to_date ? dayjs(data.on_duty_to_date) : null}
                onChange={val =>
                  setData({
                    ...data,
                    on_duty_to_date: val ? val.format("YYYY-MM-DD") : null
                  })
                }
                slotProps={{
                  textField: { ...INPUT_PROPS, InputLabelProps: { sx: LABEL_STYLE } }
                }}
              />
            </Grid>
          </LocalizationProvider>
        </Grid>

        <Typography mt={3} mb={1} sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#ffffff" }}>
          Expenses
        </Typography>

        {expenses.map((exp, idx) => (
          <Grid container spacing={1} alignItems="center" key={idx} mb={1}>
            <Grid item xs={4}>
              <TextField
                label="Expense Type"
                {...INPUT_PROPS}
                InputLabelProps={{ sx: LABEL_STYLE }}
                value={exp.expence_type}
                onChange={e =>
                  handleExpenseChange(idx, "expence_type", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Amount"
                type="number"
                {...INPUT_PROPS}
                InputLabelProps={{ sx: LABEL_STYLE }}
                value={exp.amount}
                onChange={e =>
                  handleExpenseChange(idx, "amount", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={2}>
              <Button
                variant="outlined"
                size="small"
                sx={BUTTON_STYLE}
                component="label"
              >
                Upload
                <input
                  hidden
                  type="file"
                  onChange={e =>
                    handleFileChange(idx, e.target.files[0])
                  }
                />
              </Button>
            </Grid>

           <Grid item xs={3}>
  {/* NEW FILE UPLOADED */}
  {exp.bill_attachment ? (
    <Typography
      title={exp.bill_attachment.name}
      sx={{
        fontSize: "0.8rem",
        color: "#ffffff",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }}
    >
      {exp.bill_attachment.name}
    </Typography>

  ) : exp.existing_attachment ? (
    /* EXISTING FILE FROM DB */
    <Button
      variant="outlined"
      size="small"
      sx={BUTTON_STYLE}
      //onClick={() => openAttachment(exp.existing_attachment)}
       onClick={() =>
                  openAttachment(exp.existing_attachment, exp.expence_type || "expense", exp.amount || "0")}
    >
      View Attachment
    </Button>

  ) : (
    <Typography sx={{ fontSize: "0.8rem", color: "#ffffff" }}>
      No File
    </Typography>
  )}
</Grid>

          </Grid>
        ))}

        <Button
          variant="contained"
          size="small"
          sx={{ mt: 2, fontSize: "0.8rem", textTransform: "none" }}
          onClick={addExpenseRow}
        >
          + Add Expense
        </Button>
      </Box>
    </Box>
  );
};

export default AddUpdateOnDuty;
